import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import StatTile from "shell/StatTile";

import usePayrollData, { MONTHS } from "layouts/tables/data/payrollTableData";

function Payroll() {
  const {
    columns,
    rows,
    isLoading,
    error,
    summary,
    staffUsers,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedPayroll,
    setSelectedPayroll,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddPayroll,
  } = usePayrollData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading payroll...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Payroll"
        subtitle="Record and manage staff salaries."
        actions={
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddPayroll}>
              New Payroll Entry
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatTile
            icon="payments"
            label="Total Gross"
            value={`GHS ${Number(summary.total_gross || 0).toLocaleString()}`}
            sub={`${summary.count || 0} entries`}
            tone="primary"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatTile
            icon="account_balance_wallet"
            label="Total Net Pay"
            value={`GHS ${Number(summary.total_net || 0).toLocaleString()}`}
            sub="after allowances/deductions"
            tone="success"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatTile
            icon="groups"
            label="Staff Paid"
            value={summary.count || 0}
            sub="payroll entries"
            tone="info"
          />
        </Grid>
      </Grid>

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search payroll..."
        />
      </Card>

      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedPayroll?.id ? "Edit Payroll Entry" : "New Payroll Entry"}
        onSave={handleEditSubmit}
        saveLabel={selectedPayroll?.id ? "Save Changes" : "Create Entry"}
        maxWidth="sm"
      >
        <FormSection>
          <Autocomplete
            options={staffUsers}
            getOptionLabel={(u) => (u?.id ? `${u.name} (${u.role})` : "")}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={staffUsers.find((u) => u.id === selectedPayroll?.user_id) || null}
            onChange={(_, chosen) =>
              setSelectedPayroll({
                ...selectedPayroll,
                user_id: chosen?.id || "",
                staffName: chosen ? "" : selectedPayroll?.staffName,
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Staff (system user)"
                margin="normal"
                helperText="Search by name or role — or leave blank and type a staff name below"
              />
            )}
            clearOnEscape
            fullWidth
            sx={{ mt: 1 }}
          />

          {!selectedPayroll?.user_id && (
            <TextField
              label="Staff Name"
              value={selectedPayroll?.staffName || ""}
              onChange={(e) =>
                setSelectedPayroll({ ...selectedPayroll, staffName: e.target.value })
              }
              fullWidth
              margin="normal"
            />
          )}

          <TextField
            label="Position"
            value={selectedPayroll?.position || ""}
            onChange={(e) => setSelectedPayroll({ ...selectedPayroll, position: e.target.value })}
            fullWidth
            margin="normal"
          />

          <TextField
            select
            label="Pay Frequency"
            value={selectedPayroll?.payFrequency || "monthly"}
            onChange={(e) =>
              setSelectedPayroll({ ...selectedPayroll, payFrequency: e.target.value })
            }
            fullWidth
            margin="normal"
            helperText="How often this worker is normally paid"
          >
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
          </TextField>
        </FormSection>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormSection sx={{ flex: 1, minWidth: 200 }}>
            <TextField
              label="Gross Salary (GHS)"
              type="number"
              value={selectedPayroll?.grossSalary || ""}
              onChange={(e) =>
                setSelectedPayroll({ ...selectedPayroll, grossSalary: e.target.value })
              }
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Allowances (GHS)"
              type="number"
              value={selectedPayroll?.allowances || ""}
              onChange={(e) =>
                setSelectedPayroll({ ...selectedPayroll, allowances: e.target.value })
              }
              fullWidth
              margin="normal"
            />
            <TextField
              label="Deductions (GHS)"
              type="number"
              value={selectedPayroll?.deductions || ""}
              onChange={(e) =>
                setSelectedPayroll({ ...selectedPayroll, deductions: e.target.value })
              }
              fullWidth
              margin="normal"
            />
          </FormSection>

          <FormSection sx={{ flex: 1, minWidth: 200 }}>
            <TextField
              select
              label="Reporting Month"
              value={selectedPayroll?.payPeriodMonth || 1}
              onChange={(e) =>
                setSelectedPayroll({ ...selectedPayroll, payPeriodMonth: Number(e.target.value) })
              }
              fullWidth
              margin="normal"
              helperText="Which calendar month this payment falls under (for daily/weekly pay, log one entry per payment date)"
            >
              {MONTHS.map((m, i) => (
                <MenuItem key={m} value={i + 1}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Pay Period Year"
              type="number"
              value={selectedPayroll?.payPeriodYear || new Date().getFullYear()}
              onChange={(e) =>
                setSelectedPayroll({ ...selectedPayroll, payPeriodYear: Number(e.target.value) })
              }
              fullWidth
              margin="normal"
            />
            <TextField
              select
              label="Status"
              value={selectedPayroll?.status || "pending"}
              onChange={(e) => setSelectedPayroll({ ...selectedPayroll, status: e.target.value })}
              fullWidth
              margin="normal"
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </TextField>
          </FormSection>
        </Box>

        <FormSection>
          <TextField
            label="Payment Date"
            type="date"
            value={selectedPayroll?.paymentDate || ""}
            onChange={(e) =>
              setSelectedPayroll({ ...selectedPayroll, paymentDate: e.target.value })
            }
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Notes"
            value={selectedPayroll?.notes || ""}
            onChange={(e) => setSelectedPayroll({ ...selectedPayroll, notes: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
        </FormSection>
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete the payroll entry for "${selectedPayroll?.staffName}"?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default Payroll;
